import * as React from "react";
import { Button, Input, Label, cn } from "@moanhot/ui";
import { useAdminApi, type AdminAuditEntry, type AuditFilter } from "@/api/adminClient";
import { TD, TH, when } from "@/screens/format";
import { useAdminAction, useAdminLoad } from "@/screens/hooks";
import { EmptyNote, LoadState, Page, Problem, Table } from "@/screens/ui";

function Change({ entry }: { entry: AdminAuditEntry }) {
  const after = entry.after ?? {};
  const reason = typeof after.reason === "string" ? after.reason : null;
  const detail = JSON.stringify({ before: entry.before, after: entry.after });
  return (
    <>
      {reason && <div>{reason}</div>}
      <details className="text-xs text-ink-3">
        <summary className="cursor-pointer">Before and after</summary>
        <pre className="mt-1 max-w-[60ch] overflow-x-auto whitespace-pre-wrap break-all font-mono">{detail}</pre>
      </details>
    </>
  );
}

/** Every recorded action, newest first, filtered by who, what and which tenant. */
export function Audit() {
  const api = useAdminApi();
  const [filter, setFilter] = React.useState<AuditFilter>({});
  const [load] = useAdminLoad((a) => a.listAudit(filter), [filter.actor, filter.action, filter.tenant_id], "Could not load the audit log.");
  const [more, setMore] = React.useState<{ rows: AdminAuditEntry[]; cursor: string | null } | null>(null);
  const next = useAdminAction();

  return (
    <Page title="Audit log">
      <form
        className="mb-6 flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          const data = new FormData(e.currentTarget);
          const text = (k: string) => String(data.get(k) ?? "").trim() || undefined;
          setMore(null);
          setFilter({ actor: text("actor"), action: text("action"), tenant_id: text("tenant_id") });
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="au-actor">Admin login</Label>
          <Input id="au-actor" name="actor" maxLength={64} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="au-action">Action</Label>
          <Input id="au-action" name="action" maxLength={64} placeholder="tenant.suspended" className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="au-tenant">Tenant ID</Label>
          <Input id="au-tenant" name="tenant_id" maxLength={40} placeholder="ten_..." className="font-mono" />
        </div>
        <Button type="submit" variant="secondary">
          Filter
        </Button>
      </form>
      <LoadState load={load} empty={(d) => (d.data.length === 0 ? <EmptyNote title="Nothing recorded" body="No action matches this filter." /> : null)}>
        {(d) => {
          const rows = [...d.data, ...(more?.rows ?? [])];
          const cursor = more ? more.cursor : d.next_cursor;
          return (
            <>
              <Table
                label="Audit entries"
                head={
                  <>
                    <th className={TH}>When</th>
                    <th className={TH}>Who</th>
                    <th className={TH}>Action</th>
                    <th className={TH}>Target</th>
                    <th className={TH}>Change</th>
                  </>
                }
              >
                {rows.map((r) => (
                  <tr key={r.id} className="align-top">
                    <td className={cn(TD, "whitespace-nowrap text-xs")}>{when(r.created_at)}</td>
                    <td className={TD}>
                      {r.actor ?? r.actor_type}
                      <div className="text-xs text-ink-3">{r.actor_type}</div>
                    </td>
                    <td className={cn(TD, "font-mono text-xs")}>{r.action}</td>
                    <td className={cn(TD, "font-mono text-xs")}>
                      {r.target_type} {r.target_id}
                      {r.tenant_id && <div className="text-ink-3">{r.tenant_id}</div>}
                    </td>
                    <td className={cn(TD, "text-sm")}>
                      <Change entry={r} />
                    </td>
                  </tr>
                ))}
              </Table>
              {cursor && (
                <Button
                  variant="secondary"
                  className="mt-4"
                  disabled={next.busy}
                  onClick={() => next.run(api.listAudit({ ...filter, cursor }), (page) => setMore({ rows: [...(more?.rows ?? []), ...page.data], cursor: page.next_cursor }))}
                >
                  Load older
                </Button>
              )}
              <Problem text={next.problem} />
            </>
          );
        }}
      </LoadState>
    </Page>
  );
}
